import { Component } from '@theme/component';

/**
 * <caldera-bundle-rating>
 *
 * Compact aggregated Yotpo rating for a bundle product. A bundle's own
 * `reviews.rating` metafield only reflects the bundle product's own reviews, so
 * every compact rating in the theme (buy box, product cards, upsell cards) would
 * otherwise show a single-product number. This element instead reads a data
 * island of the bundle's `global.bundle_products` child IDs, fetches each child's
 * Yotpo `bottomline` (cheap aggregate endpoint, cached + parallel), computes the
 * weighted average, and fills the pre-rendered star markup — matching the
 * aggregate shown by the full `caldera-bundle-reviews` PDP section.
 *
 * Two render modes, chosen with `data-mode`:
 *   - 'svg'  (default) — fills Horizon's `.rating` SVG stars (review-block parity)
 *   - 'pct'  — drives a `--caldera-stars-pct` overlay (caldera-upsell-card parity)
 *
 * The host starts `hidden` and only reveals once a non-zero aggregate resolves,
 * so bundles whose children have no reviews render nothing (same as the review
 * block with a blank rating).
 *
 * @typedef {object} CalderaBundleRatingRefs
 * @property {HTMLScriptElement} data
 * @property {HTMLElement} stars
 * @property {HTMLElement} [count]
 *
 * @extends {Component<CalderaBundleRatingRefs>}
 */
class CalderaBundleRating extends Component {
  requiredRefs = ['data', 'stars'];

  #appKey = '';
  /** @type {'svg' | 'pct'} */ #mode = 'svg';

  connectedCallback() {
    super.connectedCallback();
    this.#appKey = this.dataset.appKey || DEFAULT_APP_KEY;
    this.#mode = this.dataset.mode === 'pct' ? 'pct' : 'svg';
    this.#load();
  }

  async #load() {
    const ids = this.#childIds();
    if (ids.length === 0) return;

    const aggregate = await fetchAggregate(this.#appKey, ids);
    if (!aggregate) return; // no reviews → stay hidden

    if (this.#mode === 'pct') this.#renderPct(aggregate);
    else this.#renderSvg(aggregate);

    this.hidden = false;
  }

  /** @returns {number[]} */
  #childIds() {
    try {
      const parsed = JSON.parse(this.refs.data.textContent || '{}');
      const children = Array.isArray(parsed.childProducts) ? parsed.childProducts : [];
      return children.map((child) => child.id).filter((id) => typeof id === 'number');
    } catch {
      return [];
    }
  }

  /** @param {{ rating: number, count: number }} aggregate */
  #renderSvg(aggregate) {
    renderSvgRating(this, this.refs.stars, this.refs.count, aggregate);
  }

  /** @param {{ rating: number, count: number }} aggregate */
  #renderPct(aggregate) {
    renderPctRating(this, this.refs.stars, this.refs.count, aggregate);
  }
}

/**
 * <caldera-yotpo-rating>
 *
 * Live single-product Yotpo rating. Wraps star markup that Liquid pre-filled
 * from the `reviews.*` metafields and re-fills it from Yotpo's V3 storefront
 * API once it resolves. Yotpo's metafield sync excludes grouped/syndicated
 * reviews, so the server-rendered number can trail the Yotpo widget by
 * hundreds; the SSR values stay visible as the pre-JS (and API-failure)
 * fallback.
 *
 * Same two render modes as <caldera-bundle-rating>, chosen with `data-mode`:
 *   - 'svg' (default) — review-block markup; finds `.rating` / `.rating-count`
 *     when no refs are present, since the wrapped markup is upstream Horizon's
 *   - 'pct' — drives a `--caldera-stars-pct` overlay via ref="stars"/ref="count"
 *
 * `data-stars-style="single"` (review block's one-star style) skips the star
 * fill — that style's lone star doesn't map to the 5-star loop — and only
 * updates the numbers.
 *
 * @typedef {object} CalderaYotpoRatingRefs
 * @property {HTMLElement} [stars]
 * @property {HTMLElement} [count]
 *
 * @extends {Component<CalderaYotpoRatingRefs>}
 */
class CalderaYotpoRating extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  async #load() {
    const productId = Number(this.dataset.productId);
    if (!productId) return;

    const result = await fetchBottomline(this.dataset.appKey || DEFAULT_APP_KEY, productId);
    if (!result) return; // keep the SSR fallback

    const aggregate = { rating: result.averageScore, count: result.reviewCount };
    const starsEl = this.refs.stars ?? this.querySelector('.rating') ?? undefined;
    const countEl = this.refs.count ?? /** @type {HTMLElement | null} */ (this.querySelector('.rating-count')) ?? undefined;

    if (this.dataset.mode === 'pct') {
      if (starsEl instanceof HTMLElement) renderPctRating(this, starsEl, countEl, aggregate);
      return;
    }

    if (this.dataset.starsStyle === 'single') {
      const ratingNumber = countEl?.querySelector('span[aria-hidden]');
      if (ratingNumber) ratingNumber.textContent = String(Math.round(aggregate.rating * 10) / 10);
      updateCountText(this, countEl, aggregate.count);
      return;
    }

    if (starsEl instanceof HTMLElement) renderSvgRating(this, starsEl, countEl, aggregate);
    else updateCountText(this, countEl, aggregate.count);
  }
}

/**
 * Fill pre-rendered SVG stars, mirroring the half/full logic in
 * blocks/review.liquid so client-set ratings are pixel-identical to
 * server-rendered ones.
 *
 * @param {HTMLElement} root - element holding the clipPath (and optional data-reviews-label)
 * @param {HTMLElement} starsEl
 * @param {HTMLElement | undefined} countEl
 * @param {{ rating: number, count: number }} aggregate
 */
function renderSvgRating(root, starsEl, countEl, { rating, count }) {
  const decimal = rating % 1;
  let decimalRating = 0;
  if (decimal >= 0.3 && decimal <= 0.7) decimalRating = 0.5;
  else if (decimal > 0.7) decimalRating = 1;
  const filled = Math.floor(rating) + Math.ceil(decimalRating);

  // Half star: overlay a tan copy of the star clipped to its left half (the
  // clipPath the wrapped markup rendered). Mirrors blocks/review.liquid — a
  // gradient whose stops read --star-fill-color didn't resolve to the brand tan
  // on desktop, but a <use> inside .rating inherits the same tan as full stars.
  const clipId = root.querySelector('clipPath')?.id;

  const stars = starsEl.querySelectorAll('svg.stars');
  stars.forEach((star, index) => {
    const position = index + 1;
    const isHalf = decimalRating === 0.5 && position === filled;
    star.classList.toggle('filled-star', !isHalf && position <= filled);
    // Drop any SSR half-star overlay before re-applying at the right position.
    for (const overlay of star.querySelectorAll('use[clip-path]')) overlay.remove();
    if (isHalf && clipId) {
      const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      overlay.setAttribute('href', '#star');
      overlay.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#star');
      overlay.setAttribute('clip-path', `url(#${clipId})`);
      overlay.style.fill = 'var(--star-fill-color)';
      star.querySelector('use')?.insertAdjacentElement('afterend', overlay);
    }
  });

  starsEl.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars`);
  updateCountText(root, countEl, count);
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} starsEl
 * @param {HTMLElement | undefined} countEl
 * @param {{ rating: number, count: number }} aggregate
 */
function renderPctRating(root, starsEl, countEl, { rating, count }) {
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
  starsEl.style.setProperty('--caldera-stars-pct', String(pct));
  starsEl.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars`);
  if (!countEl) return;
  // With a `data-reviews-label`, render "123 reviews"; otherwise the compact "[123]".
  const label = countEl.dataset.reviewsLabel ?? root.dataset.reviewsLabel;
  countEl.textContent = label ? `${count} ${label}` : `[${count}]`;
}

/**
 * Replace a count element's text with the live count, preserving any child
 * elements (the review block's 'single' style keeps its rating span and
 * separator; plain counts are bare text).
 *
 * @param {HTMLElement} root
 * @param {HTMLElement | undefined} countEl
 * @param {number} count
 */
function updateCountText(root, countEl, count) {
  if (!countEl) return;
  const label = countEl.dataset.reviewsLabel ?? root.dataset.reviewsLabel ?? '';
  for (const node of [...countEl.childNodes]) {
    if (node.nodeType === Node.TEXT_NODE) node.remove();
  }
  countEl.append(label ? `${count} ${label}` : String(count));
}

// --- Yotpo aggregation ----------------------------------------------------

/** Public Yotpo widget key (safe to expose). Overridable via `data-app-key`. */
const DEFAULT_APP_KEY = '7MWKpDOGOknXBXpo12TeaGWtIAs67lY9tGHxmig5';

/**
 * Bottomline per product, cached by id (as a promise, so concurrent widgets
 * sharing a child dedupe onto one request).
 *
 * @type {Map<number, Promise<{ averageScore: number, reviewCount: number } | null>>}
 */
const bottomlineCache = new Map();

/** @param {string} appKey @param {number} productId */
function fetchBottomline(appKey, productId) {
  const cached = bottomlineCache.get(productId);
  if (cached) return cached;

  const request = (async () => {
    try {
      // V3 storefront API — the same source Yotpo's own widget reads, and the
      // only one matching the Yotpo admin's published counts. The v1 widget API
      // excludes Shop-app-collected reviews and the synced reviews.* metafields
      // lag further, so both trail the widget (The Good: 2093/1779 vs 2580).
      const response = await fetch(
        `https://api-cdn.yotpo.com/v3/storefront/store/${appKey}/product/${productId}/reviews?page=1&perPage=1`
      );
      if (!response.ok) return null;
      const json = await response.json();
      const bottomline = json?.bottomline;
      if (!bottomline || !bottomline.totalReview) return null;
      return { averageScore: bottomline.averageScore, reviewCount: bottomline.totalReview };
    } catch {
      return null;
    }
  })();

  bottomlineCache.set(productId, request);
  return request;
}

/**
 * Weighted average across a bundle's children: Σ(score × count) / Σ(count).
 *
 * @param {string} appKey
 * @param {number[]} productIds
 * @returns {Promise<{ rating: number, count: number } | null>}
 */
async function fetchAggregate(appKey, productIds) {
  const results = await Promise.all(productIds.map((id) => fetchBottomline(appKey, id)));

  let weightedScore = 0;
  let totalReviews = 0;
  for (const result of results) {
    if (!result) continue;
    weightedScore += result.averageScore * result.reviewCount;
    totalReviews += result.reviewCount;
  }

  if (totalReviews === 0) return null;
  return { rating: weightedScore / totalReviews, count: totalReviews };
}

// Defined last so that when a <caldera-bundle-rating> already in the DOM upgrades
// synchronously here, the module-level consts above are past their TDZ.
if (!customElements.get('caldera-bundle-rating')) {
  customElements.define('caldera-bundle-rating', CalderaBundleRating);
}
if (!customElements.get('caldera-yotpo-rating')) {
  customElements.define('caldera-yotpo-rating', CalderaYotpoRating);
}
